import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { formatToDateTwoYearAndTimeOrNull } from '~/util/date';
import { PatientNameTableCell } from '~/features/intake/patients/PatientNameTableCell';
import { formatStringToTitleCase } from '~/util/functions';
import { getChartStatusId, RenderFacilityNameCell } from './LegacyHospitalizationsEventTable';
import { useNavigate } from 'react-router-dom';
import { getPatientLegacyHospitalizationRoute } from '../util';
import { TableActions } from '~/shared/data-tables/TableActions';

const LegacyHospitalizationsDataTable = createQueryDataTable('careport-stays');
type RowType = RowOfQueryDataTable<typeof LegacyHospitalizationsDataTable>;
type TableProps = PropsOfQueryDataTable<typeof LegacyHospitalizationsDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const navigate = useNavigate();

	const onViewDetails = () => {
		navigate(getPatientLegacyHospitalizationRoute(row.sponsorMrn, row.stayUuid!));
	};

	return (
		<TableActions>
			<TableActions.ViewButton aria-label="View" onClick={onViewDetails} />
		</TableActions>
	);
}

const columnDefinitions = LegacyHospitalizationsDataTable.columns([
	{
		header: 'Patient',
		key: 'patientName',
		sortable: true,
		width: 'w-[250px]',
		renderCell: row => (
			<PatientNameTableCell
				firstName={row.patientFirstName ?? 'Unknown'}
				lastName={row.patientLastName ?? 'Unknown'}
				patientIdentifier={row.sponsorMrn}
				chartStatusId={getChartStatusId(row.chartStatus)}
			/>
		)
	},
	{
		header: 'Team',
		key: 'patientTeam',
		sortable: true,
		renderCell: row => (row.patientTeam ? formatStringToTitleCase(row.patientTeam) : 'N/A')
	},
	{
		header: 'Facility',
		key: 'facilityName',
		cellClassName: 'overflow-hidden',
		width: 'w-[250px]',
		sortable: true,
		renderCell: row => (
			<RenderFacilityNameCell
				facilityName={row.facilityName}
				facilityCity={row.facilityCity}
				facilityState={row.faciltyState}
				facilityZipCode={row.facilityZipCode}
			/>
		)
	},
	{
		header: 'Hospital Status',
		key: 'lastStatus',
		sortable: true
	},
	{
		header: 'As Of',
		key: 'lastStatusDateTime',
		sortable: true,
		renderCell: row => formatToDateTwoYearAndTimeOrNull(row.lastStatusDateTime, '-')
	},
	{
		header: 'Stay Started At',
		key: 'firstStatusDateTime',
		sortable: true,
		renderCell: row => formatToDateTwoYearAndTimeOrNull(row.firstStatusDateTime, '-')
	},
	{
		header: 'Setting',
		key: 'lastSetting',
		sortable: true
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[100px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function LegacyHospitalizationsTable(props: TableProps) {
	return (
		<LegacyHospitalizationsDataTable.Table
			columns={columnDefinitions}
			emptyMessage="No hospitalizations found"
			getRowId={row => row.stayUuid}
			{...props}
		/>
	);
}
